import { t } from '@lingui/core/macro';
import { Trans } from '@lingui/react/macro';
import { AddressRecord } from '@navio/nsdk';
import { BookingAddress, TimeZone } from 'noddi-types';
import { NoddiIcon } from 'noddi-ui-common';
import { NoddiBasicCard } from 'noddi-ui-web';
import { getBookingTimeWindowDisplayText, getInsuranceCompanyName } from 'noddi-util';
import { MapPinSelectionBookingStore } from '../../stores/MapPinSelectionBookingStore';
import { MapPinSelectionProposalStore } from '../../stores/MapPinSelectionProposalStore';
import { getRainingTexts, getTemperedTexts } from '../../utils/getInfoAboutBooking';
import { AddressLine } from '../Address/AddressLine';

type InfoSteps = 'address' | 'timeWindow' | 'communication';

interface CommonBookingInfoProps {
  address?: AddressRecord | BookingAddress;
  timeWindow?: {
    startsAt: string;
    endsAt: string;
    timeZone: TimeZone;
  };
  excludeSteps?: InfoSteps[];
  insuranceCompany?: string;
  hasStoneChipRepair?: boolean;
  isBookingProposal: boolean;
  /** Stationary customers drive to the station -- no parking pin to drop. */
  isStationary?: boolean;
  /** False when we only pick up the tires from another tire hotel: no arrival time to show or text about. */
  hasCustomerVisit?: boolean;
}

export const CommonBookingInfoCard = ({
  address,
  timeWindow,
  excludeSteps,
  insuranceCompany,
  hasStoneChipRepair,
  isBookingProposal,
  isStationary = false,
  hasCustomerVisit = true
}: CommonBookingInfoProps) => {
  const timeWindowDisplay = timeWindow
    ? getBookingTimeWindowDisplayText({ timeWindow })
    : t`Problems with the last time slot`;

  return (
    <NoddiBasicCard className='flex flex-col gap-3'>
      {timeWindow && !excludeSteps?.includes('timeWindow') && (
        <div className='flex items-baseline gap-2'>
          <NoddiIcon name='Calendar' />
          <p>{timeWindowDisplay}</p>
        </div>
      )}
      {insuranceCompany && (
        <div className='flex items-baseline gap-2'>
          <NoddiIcon name='HandCoins' />
          <p>{getInsuranceCompanyName(insuranceCompany)}</p>
        </div>
      )}
      {!hasCustomerVisit && (
        <div className='flex items-baseline gap-2'>
          <NoddiIcon name='Truck' />
          <p>
            <Trans>We'll pick up your tires from your current tire hotel and text you when they're picked up.</Trans>
          </p>
        </div>
      )}
      {hasCustomerVisit && !excludeSteps?.includes('communication') && (
        <div className='flex items-baseline gap-2'>
          <NoddiIcon name='MessageSquare' />
          <p>
            <Trans>We'll text you the day before with estimated arrival time and when you're next on the list</Trans>
          </p>
        </div>
      )}
      {hasStoneChipRepair && (
        <div className='flex items-baseline gap-2'>
          <NoddiIcon name='Info' />
          <div className='flex flex-col gap-1'>
            <p>{t`Important information`}</p>
            <div className='mt-1 flex flex-col gap-2 text-sm text-black/70'>
              <p className='text-balance'>{getRainingTexts()}</p>
              <p className='text-balance'>{getTemperedTexts()}</p>
            </div>
          </div>
        </div>
      )}
      <div className='flex flex-col gap-1'>
        {address && !excludeSteps?.includes('address') && <AddressLine address={address} />}
        {!isStationary &&
          hasCustomerVisit &&
          (isBookingProposal ? <MapPinSelectionProposalStore /> : <MapPinSelectionBookingStore />)}
      </div>
    </NoddiBasicCard>
  );
};
